import { useState, useEffect, useCallback } from 'react';
import { isApiError } from '@aiptyg/types';
import * as settingsService from '../services/settings.service';
import type { HomePopupConfigDTO, HomePopupUpdateParams } from '../types/settings.types';
import { useToastStore } from '../../../store/toast.store';

function resolveError(e: unknown): string {
  if (isApiError(e)) {
    const map: Record<string, string> = {
      admin_role_required: '无管理员权限',
      'invalid home popup': '弹窗启用时必须填写标题和文字内容，图片可选',
      network_error: '网络连接失败，请检查网络',
    };
    return map[e.message] ?? `请求失败 (${e.message})`;
  }
  return '未知错误';
}

export function useHomePopup() {
  const addToast = useToastStore((s) => s.addToast);
  const [data, setData] = useState<HomePopupConfigDTO | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await settingsService.getHomePopup());
    } catch (e) {
      setError(resolveError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const update = useCallback(async (params: HomePopupUpdateParams) => {
    setSaving(true);
    try {
      const result = await settingsService.updateHomePopup(params);
      setData(result);
      addToast('success', '首页弹窗已更新');
      return true;
    } catch (e) {
      addToast('error', resolveError(e));
      return false;
    } finally {
      setSaving(false);
    }
  }, [addToast]);

  return { data, loading, saving, error, reload: load, update };
}
